// Partner outbound-call request parser.
// Accepts the documented POST /calls shape plus common CRM aliases so
// integrations can send name/mobile/company without wrapping lead_data.
// Deno-free so Vitest can import this module.

export interface ParsedLeadFields {
  contact_name: string | null;
  first_name: string | null;
  last_name: string | null;
  company_name: string | null;
  email: string | null;
  contact_role: string | null;
  website: string | null;
  industry: string | null;
  company_size: string | null;
  notes: string | null;
  external_id: string | null;
}

export interface ParsedOutboundCall {
  phone: string | null;
  agent_id: string | null;
  from_number: string | null;
  campaign_id: string | null;
  campaign_slug: string | null;
  customer_type: string | null;
  lead_id: string | null;
  lead: ParsedLeadFields;
  variables: Record<string, string>;
  call_type: string;
  call_purpose: string | null;
  max_duration: number;
  metadata: Record<string, unknown>;
  idempotency_key: string | null;
  errors: string[];
}

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const pickString = (obj: Record<string, unknown>, keys: string[]): string | null => {
  for (const key of keys) {
    const value = obj[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  }
  return null;
};

export const flattenVariables = (raw: unknown): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(asRecord(raw))) {
    if (value === undefined || value === null) continue;
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      out[key] = String(value);
    }
  }
  return out;
};

/**
 * Normalize a phone to E.164. UK national numbers starting with 0 become +44.
 * Strips spaces, dashes, parentheses, and dots. Returns null if invalid.
 */
export const normalizeE164 = (raw: unknown, defaultCountry = '44'): string | null => {
  if (raw == null) return null;
  let input = String(raw).trim();
  if (!input) return null;
  input = input.replace(/[\s().-]/g, '');
  if (input.startsWith('00')) input = `+${input.slice(2)}`;
  if (/^0\d{9,10}$/.test(input)) {
    input = `+${defaultCountry}${input.slice(1)}`;
  } else if (/^\d{8,15}$/.test(input)) {
    input = `+${input}`;
  }
  if (!/^\+[1-9]\d{1,14}$/.test(input)) return null;
  return input;
};

export const phoneLookupVariants = (phone: string): string[] => {
  const digits = phone.replace(/[^\d]/g, '');
  const plus = digits ? `+${digits}` : '';
  const stripped = phone.replace(/[^\d+]/g, '');
  return [...new Set([phone, stripped, digits, plus].filter(Boolean))];
};

export const sanitizeIdempotencyKey = (raw: unknown): string | null => {
  if (typeof raw !== 'string') return null;
  const cleaned = raw.trim().replace(/[^a-zA-Z0-9._:-]/g, '').slice(0, 64);
  return cleaned || null;
};

const pickFromLayers = (
  root: Record<string, unknown>,
  leadData: Record<string, unknown>,
  keys: string[]
): string | null => pickString(root, keys) || pickString(leadData, keys);

const parseLeadFields = (
  root: Record<string, unknown>,
  leadData: Record<string, unknown>
): ParsedLeadFields => {
  let contactName = pickFromLayers(root, leadData, ['contact_name', 'name', 'full_name', 'fullName', 'customer_name']);
  const firstName = pickFromLayers(root, leadData, ['first_name', 'firstName', 'firstname']);
  const lastName = pickFromLayers(root, leadData, ['last_name', 'lastName', 'lastname']);
  if (!contactName && (firstName || lastName)) {
    contactName = [firstName, lastName].filter(Boolean).join(' ');
  }

  let derivedFirst = firstName;
  let derivedLast = lastName;
  if (!derivedFirst && contactName) derivedFirst = contactName.split(/\s+/)[0] || null;
  if (!derivedLast && contactName) {
    const parts = contactName.split(/\s+/);
    if (parts.length > 1) derivedLast = parts.slice(1).join(' ');
  }

  const externalId = pickFromLayers(root, leadData, ['external_id', 'externalId', 'partner_id', 'partnerId', 'crm_id', 'crmId']);
  const notes = pickFromLayers(root, leadData, ['notes']);

  return {
    contact_name: contactName,
    first_name: derivedFirst,
    last_name: derivedLast,
    company_name: pickFromLayers(root, leadData, ['company_name', 'company', 'companyName', 'organisation', 'organization', 'business_name']),
    email: pickFromLayers(root, leadData, ['email', 'email_address', 'emailAddress']),
    contact_role: pickFromLayers(root, leadData, ['contact_role', 'title', 'job_title', 'jobTitle', 'role']),
    website: pickFromLayers(root, leadData, ['website', 'website_url', 'websiteUrl', 'url']),
    industry: pickFromLayers(root, leadData, ['industry']),
    company_size: pickFromLayers(root, leadData, ['company_size', 'companySize']),
    notes: notes || (externalId ? `Partner ID: ${externalId}` : null),
    external_id: externalId,
  };
};

export const parseOutboundCallInput = (body: unknown): ParsedOutboundCall => {
  const root = asRecord(body);
  const leadData = asRecord(root.lead_data ?? root.lead);
  const errors: string[] = [];

  const phone = normalizeE164(
    pickFromLayers(root, leadData, ['phone', 'to', 'mobile', 'phone_number', 'phoneNumber', 'to_number', 'toNumber', 'mobile_number', 'msisdn'])
  );
  if (!phone) {
    errors.push('A destination phone is required (phone, to, or mobile) in E.164 format, e.g. +447911123456');
  }

  const agentId = pickString(root, ['agent_id', 'agentId', 'assistant_id', 'assistantId']);
  const fromNumber = normalizeE164(pickString(root, ['from_number', 'fromNumber', 'from', 'caller_id', 'callerId']));
  const campaignId = pickString(root, ['campaign_id', 'campaignId']);
  const campaignSlug = pickString(root, ['campaign']);
  const customerType = pickString(root, ['customer_type', 'customerType']);
  const leadId = pickString(root, ['lead_id', 'leadId']);

  const variables = {
    ...flattenVariables(root.dynamic_variables),
    ...flattenVariables(root.custom_variables),
    ...flattenVariables(root.variables),
  };

  const callType = pickString(root, ['call_type', 'callType']) || 'sales';
  const callPurpose = pickString(root, ['call_purpose', 'callPurpose', 'purpose']);
  const maxDurationRaw = root.max_duration ?? root.maxDuration;
  const maxDuration =
    typeof maxDurationRaw === 'number' && Number.isFinite(maxDurationRaw)
      ? Math.min(Math.max(Math.round(maxDurationRaw), 30), 600)
      : 300;

  const metadata = asRecord(root.metadata);
  const idempotency_key = sanitizeIdempotencyKey(
    root.idempotency_key ?? root.idempotencyKey
  );

  return {
    phone,
    agent_id: agentId,
    from_number: fromNumber,
    campaign_id: campaignId,
    campaign_slug: campaignSlug,
    customer_type: customerType,
    lead_id: leadId,
    lead: parseLeadFields(root, leadData),
    variables,
    call_type: callType,
    call_purpose: callPurpose,
    max_duration: maxDuration,
    metadata,
    idempotency_key,
    errors,
  };
};

export const buildDialDynamicVariables = (
  parsed: ParsedOutboundCall,
  extras: Record<string, string | null | undefined> = {}
): Record<string, string> => {
  const vars: Record<string, string> = {};
  const set = (key: string, value?: string | null) => {
    if (value) vars[key] = value;
  };

  const { lead, phone } = parsed;
  set('contact_name', lead.contact_name);
  set('name', lead.contact_name);
  set('first_name', lead.first_name);
  set('last_name', lead.last_name);
  set('company_name', lead.company_name);
  set('customer_name', lead.contact_name);
  set('customer_company', lead.company_name);
  set('email', lead.email);
  set('phone', phone);
  set('mobile', phone);
  set('to_number', phone);
  set('industry', lead.industry);
  set('company_size', lead.company_size);
  set('contact_role', lead.contact_role);
  set('website', lead.website);
  set('external_id', lead.external_id);
  Object.assign(vars, parsed.variables);
  for (const [key, value] of Object.entries(extras)) {
    if (value) vars[key] = value;
  }
  if (!vars.contact_name) vars.contact_name = 'there';
  return vars;
};
